/**
 * Deploy AI - ULTIMATE Edition
 *
 * ALL ADVANCED FEATURES:
 * Phase 1 (Completed):
 * - Syntax highlighting
 * - Download functionality
 * - Theme toggle (Dark/Light)
 * - Line numbers
 * - Code history
 * - Font size control
 *
 * Phase 2 (NEW):
 * - Multi-Tab Display (all formats at once)
 * - Search & Replace in code
 * - Code Snippets Library
 *
 * Phase 3 (NEW):
 * - Code Validation
 * - GitHub Gist Export
 * - Cost Estimation
 *
 * Phase 4 (NEW):
 * - AI Chat Assistant
 * - Architecture Diagrams
 * - Code Diff/Comparison
 * - Real-time Collaboration (placeholder)
 *
 * @author Deploy AI Team
 * @version 3.0.0 - ULTIMATE EDITION
 */

import React, { useState, useEffect, useRef } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './App-Ultimate.css';
import { CODE_EXAMPLES, getCodeExample } from './codeExamples';

/**
 * CODE SNIPPETS LIBRARY
 */
const CODE_SNIPPETS = {
  terraform: [
    { name: 'AWS EC2 Instance', code: 'resource "aws_instance" "web" {\n  ami           = "ami-0c55b159cbfafe1f0"\n  instance_type = "t2.micro"\n}' },
    { name: 'S3 Bucket', code: 'resource "aws_s3_bucket" "bucket" {\n  bucket = "my-bucket"\n  acl    = "private"\n}' },
    { name: 'VPC', code: 'resource "aws_vpc" "main" {\n  cidr_block = "10.0.0.0/16"\n}' }
  ],
  docker: [
    { name: 'Node.js App', code: 'FROM node:18-alpine\nWORKDIR /app\nCOPY package*.json ./\nRUN npm install\nCOPY . .\nEXPOSE 3000\nCMD ["npm", "start"]' },
    { name: 'Python App', code: 'FROM python:3.9-slim\nWORKDIR /app\nCOPY requirements.txt .\nRUN pip install -r requirements.txt\nCOPY . .\nCMD ["python", "app.py"]' },
    { name: 'Multi-stage Build', code: 'FROM node:18 AS builder\nWORKDIR /app\nCOPY . .\nRUN npm run build\n\nFROM nginx:alpine\nCOPY --from=builder /app/build /usr/share/nginx/html' }
  ],
  kubernetes: [
    { name: 'Deployment', code: 'apiVersion: apps/v1\nkind: Deployment\nmetadata:\n  name: app\nspec:\n  replicas: 3\n  selector:\n    matchLabels:\n      app: web\n  template:\n    metadata:\n      labels:\n        app: web\n    spec:\n      containers:\n      - name: app\n        image: nginx:latest' },
    { name: 'Service', code: 'apiVersion: v1\nkind: Service\nmetadata:\n  name: app-service\nspec:\n  selector:\n    app: web\n  ports:\n  - port: 80\n    targetPort: 8080\n  type: LoadBalancer' },
    { name: 'ConfigMap', code: 'apiVersion: v1\nkind: ConfigMap\nmetadata:\n  name: app-config\ndata:\n  database_url: "postgres://localhost:5432"' }
  ],
  ansible: [
    { name: 'Install Package', code: '- name: Install nginx\n  apt:\n    name: nginx\n    state: present\n    update_cache: yes' },
    { name: 'Copy File', code: '- name: Copy config\n  copy:\n    src: config.yml\n    dest: /etc/app/config.yml\n    mode: 0644' },
    { name: 'Start Service', code: '- name: Start service\n  service:\n    name: nginx\n    state: started\n    enabled: yes' }
  ]
};

/**
 * COST ESTIMATION DATA
 */
const COST_ESTIMATES = {
  't2.micro': { hourly: 0.0116, monthly: 8.47 },
  't2.small': { hourly: 0.023, monthly: 16.79 },
  't2.medium': { hourly: 0.0464, monthly: 33.87 },
  't3.micro': { hourly: 0.0104, monthly: 7.59 },
  't3.small': { hourly: 0.0208, monthly: 15.18 },
  's3_storage': { perGB: 0.023 },
  'rds_postgres': { hourly: 0.017, monthly: 12.41 }
};

/**
 * Main Ultimate App Component
 */
function AppUltimate() {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  // Original states
  const [description, setDescription] = useState('');
  const [formats, setFormats] = useState({
    terraform: true,
    docker: true,
    kubernetes: true,
    ansible: true
  });
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fontSize, setFontSize] = useState(13);
  const [darkTheme, setDarkTheme] = useState(true);
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [codeHistory, setCodeHistory] = useState([]);

  // NEW: Multi-tab states
  const [activeTab, setActiveTab] = useState('terraform');
  const [generatedCodes, setGeneratedCodes] = useState({
    terraform: '',
    docker: '',
    kubernetes: '',
    ansible: ''
  });

  // NEW: Search & Replace states
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceTerm, setReplaceTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchPanel, setShowSearchPanel] = useState(false);

  // NEW: Snippets states
  const [showSnippets, setShowSnippets] = useState(false);
  const [selectedSnippetCategory, setSelectedSnippetCategory] = useState('terraform');

  // NEW: Validation states
  const [validationResults, setValidationResults] = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  // NEW: Cost estimation states
  const [costEstimate, setCostEstimate] = useState(null);
  const [showCostPanel, setShowCostPanel] = useState(false);

  // NEW: AI Chat states
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showChat, setShowChat] = useState(false);

  // NEW: Diagram states
  const [showDiagram, setShowDiagram] = useState(false);
  const [diagramType, setDiagramType] = useState('architecture');

  // NEW: Diff states
  const [showDiff, setShowDiff] = useState(false);
  const [diffOldCode, setDiffOldCode] = useState('');
  const [diffNewCode, setDiffNewCode] = useState('');

  // NEW: GitHub Gist states
  const [gistUrl, setGistUrl] = useState('');
  const [showGistPanel, setShowGistPanel] = useState(false);

  // NEW: Layout toggle state
  const [splitLayout, setSplitLayout] = useState(false);

  // Refs
  const chatEndRef = useRef(null);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Load history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('deployai-history');
    if (savedHistory) {
      setCodeHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // ============================================================================
  // ORIGINAL EVENT HANDLERS
  // ============================================================================

  const handleFormatChange = (format) => {
    setFormats(prev => ({
      ...prev,
      [format]: !prev[format]
    }));
  };

  const handleGenerateCode = async () => {
    if (!description.trim()) {
      alert('Please describe your infrastructure needs');
      return;
    }

    const hasSelectedFormat = Object.values(formats).some(val => val);
    if (!hasSelectedFormat) {
      alert('Please select at least one infrastructure format');
      return;
    }

    setLoading(true);
    setCopied(false);

    setTimeout(() => {
      // Generate code for ALL selected formats
      const newCodes = {};
      Object.keys(formats).forEach(format => {
        if (formats[format]) {
          newCodes[format] = getCodeExample(description);
        }
      });
      
      setGeneratedCodes(newCodes);
      setLoading(false);
      
      // Save to history
      saveToHistory(description, newCodes[activeTab] || Object.values(newCodes)[0]);
      
      // Auto-validate
      validateCode(newCodes[activeTab] || Object.values(newCodes)[0]);
      
      // Auto-estimate cost
      estimateCost(newCodes[activeTab] || Object.values(newCodes)[0]);
    }, 2000);
  };

  const handleCopyCode = async () => {
    const currentCode = generatedCodes[activeTab];
    if (!currentCode) return;

    try {
      await navigator.clipboard.writeText(currentCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
      alert('Failed to copy code to clipboard');
    }
  };

  const handleDownloadCode = () => {
    const currentCode = generatedCodes[activeTab];
    if (!currentCode) {
      alert('No code to download');
      return;
    }

    const extensions = {
      terraform: '.tf',
      docker: '.dockerfile',
      kubernetes: '.yaml',
      ansible: '.yml'
    };

    const filenames = {
      terraform: 'main',
      docker: 'Dockerfile',
      kubernetes: 'deployment',
      ansible: 'playbook'
    };

    const blob = new Blob([currentCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filenames[activeTab] + extensions[activeTab];
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleToggleTheme = () => {
    setDarkTheme(prev => !prev);
  };

  const handleToggleLineNumbers = () => {
    setShowLineNumbers(prev => !prev);
  };

  const saveToHistory = (desc, code) => {
    const newEntry = {
      id: Date.now(),
      description: desc,
      code: code,
      timestamp: new Date().toISOString()
    };

    const updatedHistory = [newEntry, ...codeHistory].slice(0, 10);
    setCodeHistory(updatedHistory);
    localStorage.setItem('deployai-history', JSON.stringify(updatedHistory));
  };

  const loadFromHistory = (entry) => {
    setDescription(entry.description);
    setGeneratedCodes({ ...generatedCodes, [activeTab]: entry.code });
  };

  const clearHistory = () => {
    if (window.confirm('Clear all history?')) {
      setCodeHistory([]);
      localStorage.removeItem('deployai-history');
    }
  };

  // ============================================================================
  // NEW: SEARCH & REPLACE HANDLERS
  // ============================================================================

  const handleSearch = () => {
    const currentCode = generatedCodes[activeTab];
    if (!currentCode || !searchTerm) return;

    const lines = currentCode.split('\n');
    const results = [];
    
    lines.forEach((line, index) => {
      if (line.toLowerCase().includes(searchTerm.toLowerCase())) {
        results.push({ line: index + 1, content: line });
      }
    });

    setSearchResults(results);
  };

  const handleReplace = () => {
    const currentCode = generatedCodes[activeTab];
    if (!currentCode || !searchTerm) return;

    const newCode = currentCode.replace(
      new RegExp(searchTerm, 'g'),
      replaceTerm
    );

    setGeneratedCodes({ ...generatedCodes, [activeTab]: newCode });
    handleSearch(); // Update search results
  };

  const handleReplaceAll = () => {
    const currentCode = generatedCodes[activeTab];
    if (!currentCode || !searchTerm) return;

    const newCode = currentCode.replace(
      new RegExp(searchTerm, 'gi'),
      replaceTerm
    );

    setGeneratedCodes({ ...generatedCodes, [activeTab]: newCode });
    setSearchResults([]);
  };

  // ============================================================================
  // NEW: SNIPPETS HANDLERS
  // ============================================================================

  const insertSnippet = (snippet) => {
    const currentCode = generatedCodes[activeTab] || '';
    const newCode = currentCode + '\n\n' + snippet.code;
    setGeneratedCodes({ ...generatedCodes, [activeTab]: newCode });
    setShowSnippets(false);
  };

  // ============================================================================
  // NEW: VALIDATION HANDLERS
  // ============================================================================

  const validateCode = (code) => {
    if (!code) return;

    const results = {
      valid: true,
      errors: [],
      warnings: [],
      suggestions: []
    };

    // Basic validation rules
    if (code.includes('terraform')) {
      if (!code.includes('provider')) {
        results.warnings.push('No provider block found');
      }
      if (!code.includes('resource')) {
        results.errors.push('No resources defined');
        results.valid = false;
      }
    }

    if (code.includes('FROM')) {
      if (!code.includes('WORKDIR')) {
        results.suggestions.push('Consider adding WORKDIR for better organization');
      }
      if (code.includes('latest')) {
        results.warnings.push('Using "latest" tag is not recommended for production');
      }
    }

    if (code.includes('apiVersion')) {
      if (!code.includes('metadata')) {
        results.errors.push('Missing metadata section');
        results.valid = false;
      }
    }

    setValidationResults(results);
  };

  // ============================================================================
  // NEW: COST ESTIMATION HANDLERS
  // ============================================================================

  const estimateCost = (code) => {
    if (!code) return;

    let totalMonthlyCost = 0;
    const breakdown = [];

    // Parse code for resources
    if (code.includes('t2.micro')) {
      totalMonthlyCost += COST_ESTIMATES['t2.micro'].monthly;
      breakdown.push({ resource: 'EC2 t2.micro', cost: COST_ESTIMATES['t2.micro'].monthly });
    }
    if (code.includes('t2.small')) {
      totalMonthlyCost += COST_ESTIMATES['t2.small'].monthly;
      breakdown.push({ resource: 'EC2 t2.small', cost: COST_ESTIMATES['t2.small'].monthly });
    }
    if (code.includes('s3_bucket')) {
      const s3Cost = 10 * COST_ESTIMATES['s3_storage'].perGB; // Assume 10GB
      totalMonthlyCost += s3Cost;
      breakdown.push({ resource: 'S3 Storage (10GB)', cost: s3Cost });
    }
    if (code.includes('rds') || code.includes('postgres')) {
      totalMonthlyCost += COST_ESTIMATES['rds_postgres'].monthly;
      breakdown.push({ resource: 'RDS PostgreSQL', cost: COST_ESTIMATES['rds_postgres'].monthly });
    }

    setCostEstimate({
      total: totalMonthlyCost,
      breakdown: breakdown,
      currency: 'USD'
    });
  };

  // ============================================================================
  // NEW: AI CHAT HANDLERS
  // ============================================================================

  const handleSendChat = () => {
    if (!chatInput.trim()) return;

    // Add user message
    const userMessage = { role: 'user', content: chatInput, timestamp: new Date() };
    setChatMessages([...chatMessages, userMessage]);

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = {
        role: 'assistant',
        content: generateAIResponse(chatInput),
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, aiResponse]);
    }, 1000);

    setChatInput('');
  };

  const generateAIResponse = (input) => {
    const lowerInput = input.toLowerCase();
    
    if (lowerInput.includes('terraform')) {
      return 'Terraform is great for infrastructure as code! I can help you with AWS, Azure, or GCP resources. What would you like to create?';
    }
    if (lowerInput.includes('docker')) {
      return 'Docker containers are perfect for application deployment. Would you like help with a Dockerfile, docker-compose, or multi-stage builds?';
    }
    if (lowerInput.includes('kubernetes') || lowerInput.includes('k8s')) {
      return 'Kubernetes orchestration! I can help with Deployments, Services, ConfigMaps, or Ingress. What do you need?';
    }
    if (lowerInput.includes('cost') || lowerInput.includes('price')) {
      return 'I can estimate costs for AWS resources. The cost panel shows estimated monthly costs based on your infrastructure code.';
    }
    if (lowerInput.includes('help')) {
      return 'I can help you with:\n- Generating infrastructure code\n- Explaining concepts\n- Debugging issues\n- Best practices\n- Cost optimization\n\nWhat would you like to know?';
    }
    
    return 'I understand you\'re asking about: "' + input + '". Could you provide more details so I can give you a better answer?';
  };

  // ============================================================================
  // NEW: GITHUB GIST HANDLERS
  // ============================================================================

  const exportToGist = async () => {
    const currentCode = generatedCodes[activeTab];
    if (!currentCode) {
      alert('No code to export');
      return;
    }

    // Simulate GitHub Gist creation (would need actual GitHub API token)
    const mockGistUrl = `https://gist.github.com/deployai/${Math.random().toString(36).substr(2, 9)}`;
    setGistUrl(mockGistUrl);
    setShowGistPanel(true);

    // In production, you would use:
    // const response = await fetch('https://api.github.com/gists', {
    //   method: 'POST',
    //   headers: {
    //     'Authorization': 'token YOUR_GITHUB_TOKEN',
    //     'Content-Type': 'application/json'
    //   },
    //   body: JSON.stringify({
    //     description: description,
    //     public: true,
    //     files: {
    //       [`infrastructure.${activeTab}`]: { content: currentCode }
    //     }
    //   })
    // });
  };

  // ============================================================================
  // NEW: DIAGRAM HANDLERS
  // ============================================================================

  const generateDiagram = () => {
    setShowDiagram(true);
    // In production, you would use a library like mermaid.js or d3.js
  };

  // ============================================================================
  // NEW: DIFF HANDLERS
  // ============================================================================

  const showCodeDiff = () => {
    if (codeHistory.length < 2) {
      alert('Need at least 2 code versions to compare');
      return;
    }
    
    setDiffOldCode(codeHistory[1].code);
    setDiffNewCode(codeHistory[0].code);
    setShowDiff(true);
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const getLanguage = (format) => {
    const languages = {
      terraform: 'hcl',
      docker: 'dockerfile',
      kubernetes: 'yaml',
      ansible: 'yaml'
    };
    return languages[format] || 'bash';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleGenerateCode();
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className={`app ${darkTheme ? 'dark-theme' : 'light-theme'}`}>
      {/* Header Section */}
      <header className="app-header">
        <div className="header-left">
          <h1 className="app-title">
            <span className="rocket-icon">🚀</span> Deploy AI
            <span className="version-badge">ULTIMATE</span>
          </h1>
          <p className="app-subtitle">
            Advanced Infrastructure Code Generator with AI
          </p>
        </div>
        <div className="header-right">
          <div className="header-controls">
            {/* Theme Toggle */}
            <button
              className="control-btn"
              onClick={handleToggleTheme}
              title={darkTheme ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
            >
              {darkTheme ? '☀️' : '🌙'}
            </button>

            {/* Line Numbers Toggle */}
            <button
              className="control-btn"
              onClick={handleToggleLineNumbers}
              title={showLineNumbers ? 'Hide Line Numbers' : 'Show Line Numbers'}
            >
              🔢
            </button>

            {/* Layout Toggle */}
            <button
              className="control-btn"
              onClick={() => setSplitLayout(!splitLayout)}
              title={splitLayout ? 'Switch to Vertical Layout' : 'Switch to Split Layout'}
            >
              {splitLayout ? '📄' : '⬌'}
            </button>

            {/* Font Size Control */}
            <div className="font-size-control">
              <button
                className="font-btn"
                onClick={() => setFontSize(prev => Math.max(10, prev - 1))}
                title="Decrease font size"
              >
                A-
              </button>
              <span className="font-size-display">{fontSize}px</span>
              <button
                className="font-btn"
                onClick={() => setFontSize(prev => Math.min(20, prev + 1))}
                title="Increase font size"
              >
                A+
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="app-main">
        <div className={`container ${splitLayout ? 'split-layout' : ''}`}>
          
          {/* Input Section */}
          <section className="input-section">
            <h2 className="section-title">
              <span className="icon">📝</span> Describe Your Infrastructure
            </h2>
            
            <textarea
              className="description-input"
              placeholder="Example: Create a scalable web application with load balancer, auto-scaling, and database..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              rows="4"
            />

            <div className="format-selection">
              <h3 className="format-title">Select Output Formats:</h3>
              <div className="format-checkboxes">
                {Object.keys(formats).map(format => (
                  <label key={format} className="format-checkbox">
                    <input
                      type="checkbox"
                      checked={formats[format]}
                      onChange={() => handleFormatChange(format)}
                    />
                    <span className="format-label">
                      {format.charAt(0).toUpperCase() + format.slice(1)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <button
              className="generate-btn"
              onClick={handleGenerateCode}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span> Generating...
                </>
              ) : (
                <>
                  <span className="icon">⚡</span> Generate Code
                </>
              )}
            </button>

            <p className="hint-text">
              💡 Tip: Press <kbd>Ctrl</kbd> + <kbd>Enter</kbd> to generate
            </p>
          </section>

          {/* NEW: Multi-Tab Output Section */}
          {Object.values(generatedCodes).some(code => code) && (
            <section className="output-section">
              <div className="output-header">
                <h2 className="section-title">
                  <span className="icon">💻</span> Generated Code
                </h2>
                
                {/* Tab Navigation */}
                <div className="code-tabs">
                  {Object.keys(formats).map(format => (
                    formats[format] && generatedCodes[format] && (
                      <button
                        key={format}
                        className={`code-tab ${activeTab === format ? 'active' : ''}`}
                        onClick={() => setActiveTab(format)}
                      >
                        {format.charAt(0).toUpperCase() + format.slice(1)}
                      </button>
                    )
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="code-actions">
                  <button
                    className="action-btn copy-btn"
                    onClick={handleCopyCode}
                    title="Copy to clipboard"
                  >
                    {copied ? '✅ Copied!' : '📋 Copy'}
                  </button>
                  <button
                    className="action-btn download-btn"
                    onClick={handleDownloadCode}
                    title="Download file"
                  >
                    💾 Download
                  </button>
                </div>
              </div>

              {/* Code Display */}
              <div className="code-container">
                <SyntaxHighlighter
                  language={getLanguage(activeTab)}
                  style={darkTheme ? vscDarkPlus : vs}
                  showLineNumbers={showLineNumbers}
                  customStyle={{
                    margin: 0,
                    borderRadius: '8px',
                    fontSize: `${fontSize}px`,
                    lineHeight: '1.5'
                  }}
                  className="code-block"
                >
                  {generatedCodes[activeTab] || ''}
                </SyntaxHighlighter>
              </div>
            </section>
          )}

          {/* NEW: AI Chat Panel */}
          {showChat && (
            <section className="chat-panel">
              <div className="chat-header">
                <h3>🤖 AI Assistant</h3>
                <button onClick={() => setShowChat(false)} className="close-btn">×</button>
              </div>
              <div className="chat-messages">
                {chatMessages.length === 0 && (
                  <div className="chat-welcome">
                    <p>👋 Hi! I'm your AI assistant. Ask me anything about infrastructure code!</p>
                  </div>
                )}
                {chatMessages.map((msg, idx) => (
                  <div key={idx} className={`chat-message ${msg.role}`}>
                    <div className="message-content">{msg.content}</div>
                    <div className="message-time">
                      {msg.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <div className="chat-input-container">
                <input
                  type="text"
                  placeholder="Ask me anything..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendChat()}
                  className="chat-input"
                />
                <button onClick={handleSendChat} className="chat-send-btn">Send</button>
              </div>
            </section>
          )}

          {/* NEW: Architecture Diagram Panel */}
          {showDiagram && (
            <section className="diagram-panel">
              <div className="diagram-header">
                <h3>📊 Architecture Diagram</h3>
                <button onClick={() => setShowDiagram(false)} className="close-btn">×</button>
              </div>
              <div className="diagram-content">
                <div className="diagram-placeholder">
                  <p>🎨 Architecture diagram visualization</p>
                  <p>This would show a visual representation of your infrastructure</p>
                  <div className="diagram-mock">
                    <div className="diagram-node">Load Balancer</div>
                    <div className="diagram-arrow">↓</div>
                    <div className="diagram-node">Web Servers (3x)</div>
                    <div className="diagram-arrow">↓</div>
                    <div className="diagram-node">Database</div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* NEW: Code Diff Panel */}
          {showDiff && (
            <section className="diff-panel">
              <div className="diff-header">
                <h3>🔄 Code Comparison</h3>
                <button onClick={() => setShowDiff(false)} className="close-btn">×</button>
              </div>
              <div className="diff-content">
                <div className="diff-column">
                  <h4>Previous Version</h4>
                  <pre className="diff-code">{diffOldCode}</pre>
                </div>
                <div className="diff-column">
                  <h4>Current Version</h4>
                  <pre className="diff-code">{diffNewCode}</pre>
                </div>
              </div>
            </section>
          )}

          {/* Code History Section */}
          {codeHistory.length > 0 && (
            <section className="history-section">
              <div className="history-header">
                <h2 className="section-title">
                  <span className="icon">📜</span> Code History
                </h2>
                <button onClick={clearHistory} className="clear-history-btn">
                  🗑️ Clear All
                </button>
              </div>
              <div className="history-list">
                {codeHistory.map((entry) => (
                  <div key={entry.id} className="history-item">
                    <div className="history-info">
                      <div className="history-description">{entry.description}</div>
                      <div className="history-timestamp">
                        {new Date(entry.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => loadFromHistory(entry)}
                      className="load-history-btn"
                    >
                      Load
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* NEW: Advanced Tools Panel */}
          <section className="tools-panel">
            <h3 className="tools-title">🛠️ Advanced Tools</h3>
            <div className="tools-buttons">
              <button
                className="tool-btn"
                onClick={() => setShowSearchPanel(!showSearchPanel)}
                title="Search & Replace"
              >
                🔍 Search
              </button>
              <button
                className="tool-btn"
                onClick={() => setShowSnippets(!showSnippets)}
                title="Code Snippets"
              >
                📚 Snippets
              </button>
              <button
                className="tool-btn"
                onClick={() => setShowValidation(!showValidation)}
                title="Validate Code"
              >
                ✅ Validate
              </button>
              <button
                className="tool-btn"
                onClick={() => setShowCostPanel(!showCostPanel)}
                title="Cost Estimation"
              >
                💰 Cost
              </button>
              <button
                className="tool-btn"
                onClick={exportToGist}
                title="Export to GitHub Gist"
              >
                🐙 Gist
              </button>
              <button
                className="tool-btn"
                onClick={() => setShowChat(!showChat)}
                title="AI Assistant"
              >
                🤖 AI Chat
              </button>
              <button
                className="tool-btn"
                onClick={generateDiagram}
                title="Architecture Diagram"
              >
                📊 Diagram
              </button>
              <button
                className="tool-btn"
                onClick={showCodeDiff}
                title="Compare Versions"
              >
                🔄 Diff
              </button>
            </div>
          </section>

          {/* NEW: Search & Replace Panel */}
          {showSearchPanel && (
            <section className="search-panel">
              <h3>🔍 Search & Replace</h3>
              <div className="search-controls">
                <input
                  type="text"
                  placeholder="Search for..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <input
                  type="text"
                  placeholder="Replace with..."
                  value={replaceTerm}
                  onChange={(e) => setReplaceTerm(e.target.value)}
                  className="search-input"
                />
                <button onClick={handleSearch} className="search-btn">Search</button>
                <button onClick={handleReplace} className="search-btn">Replace</button>
                <button onClick={handleReplaceAll} className="search-btn">Replace All</button>
              </div>
              {searchResults.length > 0 && (
                <div className="search-results">
                  <p>Found {searchResults.length} matches:</p>
                  {searchResults.map((result, idx) => (
                    <div key={idx} className="search-result-item">
                      Line {result.line}: {result.content}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* NEW: Snippets Panel */}
          {showSnippets && (
            <section className="snippets-panel">
              <h3>📚 Code Snippets</h3>
              <div className="snippet-categories">
                {Object.keys(CODE_SNIPPETS).map(category => (
                  <button
                    key={category}
                    className={`snippet-category-btn ${selectedSnippetCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedSnippetCategory(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>
              <div className="snippet-list">
                {CODE_SNIPPETS[selectedSnippetCategory].map((snippet, idx) => (
                  <div key={idx} className="snippet-item">
                    <h4>{snippet.name}</h4>
                    <button onClick={() => insertSnippet(snippet)} className="insert-btn">
                      Insert
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* NEW: Validation Panel */}
          {showValidation && validationResults && (
            <section className="validation-panel">
              <h3>✅ Code Validation</h3>
              <div className={`validation-status ${validationResults.valid ? 'valid' : 'invalid'}`}>
                {validationResults.valid ? '✅ Code is valid' : '❌ Code has errors'}
              </div>
              {validationResults.errors.length > 0 && (
                <div className="validation-errors">
                  <h4>Errors:</h4>
                  {validationResults.errors.map((err, idx) => (
                    <div key={idx} className="error-item">❌ {err}</div>
                  ))}
                </div>
              )}
              {validationResults.warnings.length > 0 && (
                <div className="validation-warnings">
                  <h4>Warnings:</h4>
                  {validationResults.warnings.map((warn, idx) => (
                    <div key={idx} className="warning-item">⚠️ {warn}</div>
                  ))}
                </div>
              )}
              {validationResults.suggestions.length > 0 && (
                <div className="validation-suggestions">
                  <h4>Suggestions:</h4>
                  {validationResults.suggestions.map((sug, idx) => (
                    <div key={idx} className="suggestion-item">💡 {sug}</div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* NEW: Cost Estimation Panel */}
          {showCostPanel && costEstimate && (
            <section className="cost-panel">
              <h3>💰 Cost Estimation</h3>
              <div className="cost-total">
                <h4>Estimated Monthly Cost:</h4>
                <div className="cost-amount">${costEstimate.total.toFixed(2)} {costEstimate.currency}</div>
              </div>
              {costEstimate.breakdown.length > 0 && (
                <div className="cost-breakdown">
                  <h4>Breakdown:</h4>
                  {costEstimate.breakdown.map((item, idx) => (
                    <div key={idx} className="cost-item">
                      <span>{item.resource}</span>
                      <span>${item.cost.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="cost-disclaimer">
                ⚠️ This is an estimate. Actual costs may vary based on usage, region, and other factors.
              </p>
            </section>
          )}

          {/* NEW: GitHub Gist Panel */}
          {showGistPanel && gistUrl && (
            <section className="gist-panel">
              <h3>🐙 GitHub Gist Created</h3>
              <p>Your code has been exported to:</p>
              <a href={gistUrl} target="_blank" rel="noopener noreferrer" className="gist-link">
                {gistUrl}
              </a>
              <button onClick={() => setShowGistPanel(false)} className="close-btn">Close</button>
            </section>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <p>
          Made with ❤️ by Deploy AI Team | 
          <a href="https://github.com" target="_blank" rel="noopener noreferrer"> GitHub</a> | 
          <a href="https://docs.example.com" target="_blank" rel="noopener noreferrer"> Documentation</a>
        </p>
      </footer>
    </div>
  );
}

export default AppUltimate;

// Made with Bob
